import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, AlertTriangle, Check, X, Pencil, Undo2, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/constants";

const MATCH_TONE = {
  exact: "bg-emerald-100 text-emerald-800",
  high: "bg-emerald-100 text-emerald-800",
  new: "bg-sky-100 text-sky-800",
  ambiguous: "bg-amber-100 text-amber-800",
  possible_duplicate: "bg-amber-100 text-amber-800",
  none: "bg-rose-100 text-rose-800",
  no_target: "bg-slate-100 text-slate-600",
};
const MATCH_LABEL = {
  exact: "Exact match", high: "Confident match", new: "New client",
  ambiguous: "Ambiguous", possible_duplicate: "Possible duplicate",
  none: "No match", no_target: "No client",
};
const ROW_STATUS_TONE = {
  done: "text-emerald-700", failed: "text-rose-700", dropped: "text-slate-400",
  pending: "text-slate-600", skipped_changed_since: "text-amber-700",
  reverted: "text-emerald-700", irreversible: "text-amber-700",
};
const CONTROL_KEYS = new Set(["client_id", "opportunity_id", "confirm"]);

// The API permits approve/undo/edit for editors and admins (require_editor:
// user.role in {admin, user}). The UI reads the SAME source (user.role) so a
// viewer sees the read-only history but not the action controls, and anyone
// who can act can always reach and use this page.
const EDITOR_ROLES = ["admin", "user"];
function canActOnPlans(user) {
  return EDITOR_ROLES.includes((user?.role || "").toLowerCase());
}

function ActionField({ label, children, testId }) {
  return <div className="text-[11px]" data-testid={testId}><span className="text-slate-500">{label}: </span>{children}</div>;
}

// Transient gateway failures (502/503/504) are the documented production cold-start /
// worker-saturation pattern (see lib/workspaceFetch.js). The plan-detail load had NO
// retry, so a cold backend made "Could not load the plan (error 502)" on the FIRST hit
// even though a retry a second later succeeds. Retry those specific statuses with backoff
// so the plan actually opens; do NOT retry 4xx (real client/auth errors surface at once).
async function fetchPlanWithRetry(url, { attempts = 4, credentials = "include", onAttempt } = {}) {
  const backoff = [600, 1500, 3000];
  let last = null;
  for (let i = 0; i < attempts; i++) {
    try {
      const r = await fetch(url, { credentials });
      if (r.ok) return { ok: true, res: r };
      // Only transient gateway/unavailable statuses are worth retrying.
      if (![502, 503, 504].includes(r.status) || i === attempts - 1) return { ok: false, status: r.status };
      last = r.status;
    } catch (e) {
      if (i === attempts - 1) return { ok: false, networkError: true };
    }
    if (onAttempt) onAttempt(i + 2, attempts);  // next attempt number / total
    await new Promise((res) => setTimeout(res, backoff[Math.min(i, backoff.length - 1)]));
  }
  return { ok: false, status: last };
}

export default function PlanReview({ planId, onChanged, user }) {
  const canAct = canActOnPlans(user);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [retryInfo, setRetryInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [undoResult, setUndoResult] = useState(null);
  // Progressive render: paint the header + questions + first rows immediately,
  // then reveal the rest on the next tick so a long plan (30+ rows) does not
  // block the first paint and read as a broken spinner.
  const [visibleCount, setVisibleCount] = useState(8);
  // Canonical picklists so stage/currency are CHOSEN, not typed (backend /vocab
  // is the same source the write path validates against).
  const [vocab, setVocab] = useState({ stages: [], statuses: [], currencies: [] });
  useEffect(() => {
    fetch(`${API}/ask-rex/plans/vocab`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setVocab(d); })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setRetryInfo(null);
    try {
      const r = await fetchPlanWithRetry(`${API}/ask-rex/plans/${planId}`, {
        onAttempt: (n, of) => setRetryInfo({ n, of }),
      });
      if (r.ok) { setPlan(await r.res.json()); }
      else if (r.networkError) { setLoadError("Could not load the plan. Check your connection and try again."); }
      else if ([502, 503, 504].includes(r.status)) {
        setLoadError(`The server was briefly unavailable while opening this plan (error ${r.status}). It is usually a cold start — press Retry.`);
      } else { setLoadError(`Could not load the plan (error ${r.status}).`); }
    } catch (e) {
      setLoadError("Could not load the plan. Check your connection and try again.");
    } finally { setLoading(false); setRetryInfo(null); }
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  // Once the plan is loaded, reveal any remaining rows after the first paint.
  useEffect(() => {
    if (!plan) return;
    const total = (plan.rows || []).length;
    if (visibleCount >= total) return;
    const id = setTimeout(() => setVisibleCount(total), 50);
    return () => clearTimeout(id);
  }, [plan, visibleCount]);

  const patchRow = async (rowId, body) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/ask-rex/plans/${planId}/rows/${rowId}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || "Could not update the row"); }
      setPlan(await r.json());
      onChanged && onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const approve = async () => {
    const blockedCount = (plan?.rows || []).filter((r) => r.status === "blocked").length;
    const msg = blockedCount > 0
      ? `Run every runnable row now? ${blockedCount} blocked row(s) will be skipped until you resolve their client.`
      : "Run every active row in this plan now?";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/ask-rex/plans/${planId}/approve`, { method: "POST", credentials: "include" });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || "Approval failed"); }
      const res = await r.json();
      toast.success(`Plan run: ${res.succeeded} succeeded, ${res.failed} failed${res.blocked ? `, ${res.blocked} blocked (skipped)` : ""}`);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const undo = async () => {
    if (!window.confirm("Undo this batch? Records changed since will be left alone.")) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/ask-rex/plans/${planId}/undo`, { method: "POST", credentials: "include" });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || "Undo failed"); }
      const res = await r.json();
      setUndoResult(res);
      toast.success(res.message);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const startEdit = (row) => {
    setEditRow(row.row_id);
    const vals = {};
    Object.entries(row.params || {}).forEach(([k, v]) => { if (!CONTROL_KEYS.has(k)) vals[k] = v; });
    setEditVals(vals);
  };
  const saveEdit = async (rowId) => { await patchRow(rowId, { params: editVals }); setEditRow(null); };
  const decideDuplicate = async (rowId, decision) => { await patchRow(rowId, { duplicate_decision: decision }); };

  if (loading) return (
    <div className="p-4 text-sm text-slate-600 flex items-center gap-2" data-testid="plan-loading">
      <Loader2 className="w-4 h-4 animate-spin" />
      <span>{retryInfo ? `Server busy — retrying (${retryInfo.n}/${retryInfo.of})…` : "Loading plan and its actions..."}</span>
    </div>
  );
  if (loadError) return (
    <div className="p-4" data-testid="plan-load-error">
      <p className="text-sm text-rose-600 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{loadError}</p>
      <Button size="sm" variant="outline" className="mt-2" onClick={load} data-testid="plan-retry">Retry</Button>
    </div>
  );
  if (!plan) return (
    <div className="p-4" data-testid="plan-load-error">
      <p className="text-sm text-rose-600">Plan not found.</p>
      <Button size="sm" variant="outline" className="mt-2" onClick={load} data-testid="plan-retry">Retry</Button>
    </div>
  );

  const isDraft = plan.status === "draft";
  const executed = ["executed", "executed_with_failures"].includes(plan.status);
  const activeRows = plan.rows.filter((r) => r.status !== "dropped");
  const runnableRows = activeRows.filter((r) => r.status !== "blocked");
  const blockedRows = plan.rows.filter((r) => r.status === "blocked");

  return (
    <div className="space-y-3" data-testid={`plan-review-${plan.plan_id}`}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900" data-testid="plan-summary">{plan.summary}</p>
          <p className="text-[11px] text-slate-500">
            {activeRows.length} action{activeRows.length === 1 ? "" : "s"} · status: <span className="font-medium">{plan.status.replace(/_/g, " ")}</span>
            {blockedRows.length > 0 ? <span className="text-rose-700 font-medium"> · {blockedRows.length} blocked</span> : ""}
            {plan.rows.filter((r) => r.status === "duplicate_review").length > 0 ? <span className="text-amber-700 font-medium"> · {plan.rows.filter((r) => r.status === "duplicate_review").length} possible duplicate</span> : ""}
            {plan.source ? ` · from ${plan.source}` : ""}{plan.batch_id ? ` · ${plan.batch_id}` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          {isDraft && canAct && <Button size="sm" onClick={approve} disabled={busy || activeRows.length === 0} data-testid="plan-approve">{busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}Approve and run</Button>}
          {executed && canAct && <Button size="sm" variant="outline" onClick={undo} disabled={busy} data-testid="plan-undo"><Undo2 className="w-3.5 h-3.5 mr-1" />Undo this batch</Button>}
          {!canAct && (isDraft || executed) && <span className="text-[11px] text-slate-500" data-testid="plan-readonly-note">Read only. Ask an editor or admin to approve or undo.</span>}
        </div>
      </div>

      {/* B5: uncertainties surfaced as questions alongside the plan */}
      {(plan.questions || []).length > 0 && (
        <Card className="border-amber-300 bg-amber-50" data-testid="plan-questions">
          <CardContent className="p-3">
            <p className="text-[11px] font-semibold text-amber-800 flex items-center gap-1.5"><HelpCircle className="w-3.5 h-3.5" />Before I run this, a few questions</p>
            <ul className="mt-1 list-disc pl-5 text-[12px] text-amber-800 space-y-0.5">
              {plan.questions.map((q, i) => <li key={i} data-testid={`plan-question-${i}`}>{q}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}

      {undoResult && (
        <Card className="border-slate-300 bg-slate-50" data-testid="plan-undo-result">
          <CardContent className="p-3 text-[12px] text-slate-700 space-y-1">
            <p className="font-semibold">{undoResult.message}</p>
            {undoResult.skipped_changed_since?.length > 0 && <p>Left alone (changed since): {undoResult.skipped_changed_since.map((x) => x.label).join(", ")}</p>}
            {undoResult.irreversible?.length > 0 && <p>Could not reverse: {undoResult.irreversible.map((x) => `${x.label} (${x.reason})`).join("; ")}</p>}
          </CardContent>
        </Card>
      )}

      {blockedRows.length > 0 && (
        <Card className="border-rose-300 bg-rose-50" data-testid="plan-blocked-banner">
          <CardContent className="p-3">
            <p className="text-[11px] font-semibold text-rose-800 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />{blockedRows.length} row(s) are blocked and will NOT run</p>
            <p className="mt-1 text-[12px] text-rose-800">Each has a client that did not resolve. Edit the row to assign an existing client, or add a Create Client action, then it will run.</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2" data-testid="plan-rows">
        {plan.rows.slice(0, visibleCount).map((row) => {
          const dropped = row.status === "dropped";
          const blocked = row.status === "blocked";
          const dupReview = row.status === "duplicate_review";
          const dup = row.duplicate_candidate || {};
          const resolved = row.resolved || {};
          const editable = Object.entries(row.params || {}).filter(([k]) => !CONTROL_KEYS.has(k));
          const visibleFieldNotes = (row.field_notes || []).filter((fn) => !fn.startsWith("BLOCKED:") && !fn.startsWith("DUPLICATE:"));
          return (
            <Card key={row.row_id} className={`border ${dropped ? "opacity-50 border-slate-200" : blocked ? "border-rose-300" : dupReview ? "border-amber-400" : "border-slate-300"}`} data-testid={`plan-row-${row.row_id}`}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="inline-block rounded bg-slate-900 text-white text-[10px] font-bold px-1.5 py-0.5">{row.action.replace(/_/g, " ")}</span>
                    <span className="text-[11px] text-slate-500">{row.target_type}</span>
                    {blocked && <span className="inline-block rounded bg-rose-600 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid={`plan-row-blocked-${row.row_id}`}>BLOCKED</span>}
                    {dupReview && <span className="inline-block rounded bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid={`plan-row-duplicate-${row.row_id}`}>POSSIBLE DUPLICATE</span>}
                    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] ${MATCH_TONE[resolved.match_status] || "bg-slate-100 text-slate-600"}`} data-testid={`plan-row-match-${row.row_id}`}>
                      {MATCH_LABEL[resolved.match_status] || resolved.match_status || "—"}
                      {resolved.company_name ? `: ${resolved.company_name}` : ""}
                      {resolved.confidence ? ` (${Math.round(resolved.confidence * 100)}%)` : ""}
                    </span>
                    {(executed || plan.status === "undone") && row.status !== "dropped" && (
                      <span className={`text-[11px] font-medium ${ROW_STATUS_TONE[row.undo_status || row.status] || "text-slate-600"}`} data-testid={`plan-row-status-${row.row_id}`}>
                        {row.undo_status ? row.undo_status.replace(/_/g, " ") : row.status}{row.error ? `: ${row.error}` : ""}
                      </span>
                    )}
                  </div>
                  {isDraft && canAct && (
                    <div className="flex gap-1">
                      {!dropped && <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => startEdit(row)} data-testid={`plan-row-edit-${row.row_id}`}><Pencil className="w-3 h-3" /></Button>}
                      <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => patchRow(row.row_id, { drop: !dropped })} data-testid={`plan-row-drop-${row.row_id}`}>
                        {dropped ? <span className="text-[10px]">restore</span> : <X className="w-3 h-3" />}
                      </Button>
                    </div>
                  )}
                </div>

                {resolved.match_status === "ambiguous" && (resolved.candidates || []).length > 0 && (
                  <p className="mt-1 text-[11px] text-amber-700">Did you mean: {resolved.candidates.map((c) => c.company_name).join(", ")}? Edit the row to point it at the right client.</p>
                )}

                {editRow === row.row_id ? (
                  <div className="mt-2 space-y-1.5" data-testid={`plan-row-editor-${row.row_id}`}>
                    {(resolved.match_status === "ambiguous") && (resolved.candidates || []).length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-slate-500 w-40 shrink-0">pick client</span>
                        <select
                          className="h-7 text-[12px] border border-amber-400 rounded px-1 flex-1"
                          value=""
                          onChange={(e) => e.target.value && setEditVals((p) => ({ ...p, client_name: e.target.value }))}
                          data-testid={`plan-row-candidate-${row.row_id}`}>
                          <option value="">Choose the right client…</option>
                          {resolved.candidates.map((c) => (
                            <option key={c.client_id || c.company_name} value={c.company_name}>{c.company_name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {editable.map(([k]) => {
                      const isStage = k === "stage";
                      const isCcy = k === "opportunity_currency" || k === "currency";
                      const isStatus = k === "status";
                      const opts = isStage ? vocab.stages : isCcy ? vocab.currencies : isStatus ? vocab.statuses : null;
                      return (
                        <div key={k} className="flex items-center gap-2">
                          <span className="text-[11px] text-slate-500 w-40 shrink-0">{k}</span>
                          {opts ? (
                            <select
                              className="h-7 text-[12px] border border-slate-300 rounded px-1 flex-1"
                              value={editVals[k] ?? ""}
                              onChange={(e) => setEditVals((p) => ({ ...p, [k]: e.target.value }))}
                              data-testid={`plan-row-field-${row.row_id}-${k}`}>
                              <option value="">{isCcy ? "Select currency…" : isStatus ? "Select status…" : "Select stage…"}</option>
                              {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <Input value={editVals[k] ?? ""} onChange={(e) => setEditVals((p) => ({ ...p, [k]: e.target.value }))} className="h-7 text-[12px]" data-testid={`plan-row-field-${row.row_id}-${k}`} />
                          )}
                        </div>
                      );
                    })}
                    <div className="flex gap-2">
                      <Button size="sm" className="h-7 text-[11px]" onClick={() => saveEdit(row.row_id)} disabled={busy} data-testid={`plan-row-save-${row.row_id}`}>Save row</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setEditRow(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                    {editable.map(([k, v]) => {
                      const old = (row.old_values || {})[k];
                      const isUpdate = row.action.startsWith("UPDATE") && k in (row.old_values || {});
                      return (
                        <ActionField key={k} label={k} testId={`plan-row-value-${row.row_id}-${k}`}>
                          {isUpdate ? (
                            <span><span className="line-through text-slate-400">{String(old ?? "—")}</span> <span className="text-slate-400">→</span> <span className="font-medium text-slate-900">{String(v)}</span></span>
                          ) : (
                            <span className="font-medium text-slate-900">{String(v)}</span>
                          )}
                        </ActionField>
                      );
                    })}
                  </div>
                )}
                {row.note && <p className="mt-1 text-[11px] italic text-slate-500">{row.note}</p>}
                {blocked && row.blocked_reason && (
                  <div className="mt-1.5 rounded border border-rose-300 bg-rose-50 px-2 py-1.5" data-testid={`plan-row-blocked-reason-${row.row_id}`}>
                    <p className="text-[11px] text-rose-800 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{row.blocked_reason}</p>
                  </div>
                )}
                {dupReview && (
                  <div className="mt-1.5 rounded border border-amber-400 bg-amber-50 px-2 py-2" data-testid={`plan-row-duplicate-panel-${row.row_id}`}>
                    <p className="text-[11px] font-semibold text-amber-900 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      This looks like an engagement already on record (matched on {(dup.matched_on || []).join(", ") || "similarity"}). Nothing is created until you choose.
                    </p>
                    <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="rounded border border-slate-300 bg-white px-2 py-1.5" data-testid={`plan-row-dup-existing-${row.row_id}`}>
                        <p className="text-[10px] uppercase tracking-wide text-slate-500">Existing record</p>
                        <p className="text-[12px] font-medium text-slate-900">{dup.name || "—"}</p>
                        <p className="text-[11px] text-slate-600">{dup.currency || ""} {dup.value != null ? Number(dup.value).toLocaleString() : "no value"}{dup.stage ? ` · ${dup.stage}` : ""}</p>
                      </div>
                      <div className="rounded border border-amber-300 bg-white px-2 py-1.5" data-testid={`plan-row-dup-proposed-${row.row_id}`}>
                        <p className="text-[10px] uppercase tracking-wide text-slate-500">Proposed from the sheet</p>
                        <p className="text-[12px] font-medium text-slate-900">{row.params?.opportunity_name || row.params?.name || "—"}</p>
                        <p className="text-[11px] text-slate-600">{row.params?.opportunity_currency || row.params?.currency || ""} {row.params?.opportunity_value != null ? Number(row.params.opportunity_value).toLocaleString() : "no value"}{row.params?.stage ? ` · ${row.params.stage}` : ""}</p>
                      </div>
                    </div>
                    {canAct && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <Button size="sm" className="h-7 text-[11px]" onClick={() => decideDuplicate(row.row_id, "update")} disabled={busy} data-testid={`plan-row-dup-update-${row.row_id}`}>Update existing</Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => decideDuplicate(row.row_id, "skip")} disabled={busy} data-testid={`plan-row-dup-skip-${row.row_id}`}>Skip (already correct)</Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px] border-amber-400 text-amber-800" onClick={() => decideDuplicate(row.row_id, "create")} disabled={busy} data-testid={`plan-row-dup-create-${row.row_id}`}>Create anyway (separate)</Button>
                      </div>
                    )}
                  </div>
                )}
                {visibleFieldNotes.length > 0 && (
                  <div className="mt-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5" data-testid={`plan-row-fieldnotes-${row.row_id}`}>
                    {visibleFieldNotes.map((fn, i) => (
                      <p key={i} className="text-[11px] text-amber-800 flex items-start gap-1">
                        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{fn}
                      </p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {visibleCount < plan.rows.length && (
          <div className="text-[11px] text-slate-500 flex items-center gap-2" data-testid="plan-rows-loading-more">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />Loading the remaining {plan.rows.length - visibleCount} row(s)...
          </div>
        )}
      </div>
      {isDraft && activeRows.length === 0 && <p className="text-[12px] text-slate-500 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />Every row is dropped. Nothing to run.</p>}
    </div>
  );
}
